import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import {
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
  getByAreaData,
} from "actions";

const REGULAR_SEASON_ID = "cmp4yz2lj00017rtcc4ok0jgv";
const REGULAR_BY_AREA_PAGE = "/game/pictree/ntt/photos/by-area";
const REGULAR_LAYER_OPTIONS = [
  { name: "都道府県", value: "regular_1_l1" },
  { name: "医療圏", value: "regular_1_l2" },
  { name: "市区町村", value: "regular_1_l3" },
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const getDateOnly = (value) => String(value || "").split(" ")[0];
const clampSeasonEndToToday = (seasonEnd, todayFormatted) => {
  if (!seasonEnd) {
    return todayFormatted;
  }

  const endDate = getDateOnly(seasonEnd);
  return moment(endDate).isAfter(todayFormatted) ? todayFormatted : endDate;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
  })
);

const PeriodNtt = ({
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
  getByAreaData,
  seasonData,
  selectedSeason,
  selectedAreaLayerId,
  dateRangePicTreeNtt,
  singlePeriod,
  currentPage,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const hasFetchedInitialByAreaRef = useRef(false);
  const [seasonOption, setSeasonOption] = useState({
    name: "レギュラーシーズン1",
    value: REGULAR_SEASON_ID,
  });

  useEffect(() => {
    getSeasonDataNtt();
  }, []);

  useEffect(() => {
    if (seasonData.length > 0) {
      const matchedSeasons = seasonData.filter((season) => {
        const startDate = season.seasonStart;
        const endDate = season.seasonEnd;
        return today >= startDate && today <= endDate;
      });
      if (!selectedSeason || selectedSeason.length === 0) {
        if (matchedSeasons.length > 0) {
          setSelectedSeasonNtt(matchedSeasons);
          if (matchedSeasons[0]?.seasonId === REGULAR_SEASON_ID) {
            setSelectedAreaLayerNtt("regular_1_l1");
          }
          const earliestStart = matchedSeasons
            .map((s) => s.seasonStart)
            .reduce((a, b) => (a < b ? a : b));
          const latestEnd = matchedSeasons
            .map((s) => s.seasonEnd)
            .reduce((a, b) => (a > b ? a : b));
          const selectedLatestEnd = matchedSeasons.some((season) => season.seasonId === REGULAR_SEASON_ID)
            ? clampSeasonEndToToday(latestEnd, todayFormatted)
            : getDateOnly(latestEnd);

          setDateRangePicTreeMinMax([
            getDateOnly(earliestStart),
            selectedLatestEnd,
          ]);
          setDateRangePicTreeNtt([
            getDateOnly(earliestStart),
            selectedLatestEnd,
          ]);

          if (
            currentPage === "/game/pictree/ntt/photos/by-area" &&
            !hasFetchedInitialByAreaRef.current
          ) {
            hasFetchedInitialByAreaRef.current = true;
            getByAreaData("ntt");
          }
        } else {
          setSelectedSeasonNtt([seasonData[0]]);
          if (seasonData[0]?.seasonId === REGULAR_SEASON_ID) {
            setSelectedAreaLayerNtt("regular_1_l1");
          }
          const endDate = seasonData[0]?.seasonId === REGULAR_SEASON_ID
            ? clampSeasonEndToToday(seasonData[0]["seasonEnd"], todayFormatted)
            : getDateOnly(seasonData[0]["seasonEnd"]);
          setDateRangePicTreeNtt([
            getDateOnly(seasonData[0]["seasonStart"]),
            endDate,
          ]);
          setDateRangePicTreeMinMax([
            getDateOnly(seasonData[0]["seasonStart"]),
            endDate,
          ]);

          if (
            currentPage === "/game/pictree/ntt/photos/by-area" &&
            !hasFetchedInitialByAreaRef.current
          ) {
            hasFetchedInitialByAreaRef.current = true;
            getByAreaData("ntt");
          }
        }
      }
    }
  }, [seasonData, currentPage, selectedSeason]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        // !moment(option.seasonStart).isAfter(currentDateTime) &&
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));
      const selectedLatestEnd = selectedSeasonsData.some((season) => season.seasonId === REGULAR_SEASON_ID)
        ? clampSeasonEndToToday(latestEnd, todayFormatted)
        : getDateOnly(latestEnd);

      setDateRangePicTreeMinMax([
        getDateOnly(earliestStart),
        selectedLatestEnd,
      ]);
      setDateRangePicTreeNtt([
        getDateOnly(earliestStart),
        selectedLatestEnd,
      ]);

      // 範囲内のシーズンを取得
      // const autoSelectedSeasons = seasonData.filter(
      //   (s) =>
      //     moment(s.seasonStart).isSameOrAfter(earliestStart) &&
      //     moment(s.seasonEnd).isSameOrBefore(latestEnd)
      // );

      // // 現在の選択に追加
      // const updatedSelections = [...validSelections, ...autoSelectedSeasons].filter(
      //   (option, index, self) =>
      //     self.findIndex((o) => o.seasonId === option.seasonId) === index
      // );

      // ステートを更新
      setSelectedSeasonNtt(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeNtt([]);
    }
  };

  const handleChange = (event) => {
    setSeasonOption(event.target);
    if (event.target.value === "cm46zidp30003bpp6cxq101mo") { // akita
      setSelectedSeasonNtt([seasonData[8]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[8]["seasonStart"]),
        getDateOnly(seasonData[8]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[8]["seasonStart"]),
        getDateOnly(seasonData[8]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cm64mqm94000br3n0esu8c0n4") { // kanto
      setSelectedSeasonNtt([seasonData[7]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[7]["seasonStart"]),
        getDateOnly(seasonData[7]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[7]["seasonStart"]),
        getDateOnly(seasonData[7]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmadtlnih0001n3mx1wvl9zog") { // aomori
      setSelectedSeasonNtt([seasonData[6]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[6]["seasonStart"]),
        getDateOnly(seasonData[6]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[6]["seasonStart"]),
        getDateOnly(seasonData[6]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmd5oyfft0001iho10o2iceha") { // iwate
      setSelectedSeasonNtt([seasonData[5]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[5]["seasonStart"]),
        getDateOnly(seasonData[5]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[5]["seasonStart"]),
        getDateOnly(seasonData[5]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmb68qvei00011vo0e591229z") { // hokkaido
      setSelectedSeasonNtt([seasonData[4]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[4]["seasonStart"]),
        getDateOnly(seasonData[4]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[4]["seasonStart"]),
        getDateOnly(seasonData[4]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmg7iw3va00017bow596s39cs") { // nagano.yamanashi.sataima
      setSelectedSeasonNtt([seasonData[3]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[3]["seasonStart"]),
        getDateOnly(seasonData[3]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[3]["seasonStart"]),
        getDateOnly(seasonData[3]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmg7iw3vc00037bowekjy65pc") { // tochigi
      setSelectedSeasonNtt([seasonData[2]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[2]["seasonStart"]),
        getDateOnly(seasonData[2]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[2]["seasonStart"]),
        getDateOnly(seasonData[2]["seasonEnd"]),
      ]);
    } else if (event.target.value === "cmkdvml9l0001ngs48zepg7ne") { // tokyo, kanagawa, chiba
      setSelectedSeasonNtt([seasonData[1]]);
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[1]["seasonStart"]),
        getDateOnly(seasonData[1]["seasonEnd"]),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[1]["seasonStart"]),
        getDateOnly(seasonData[1]["seasonEnd"]),
      ]);
      setSelectedAreaLayerNtt("regular_1_l1");
    } else if (event.target.value === REGULAR_SEASON_ID) { // regular season 1
      setSelectedSeasonNtt([seasonData[0]]);
      setSelectedAreaLayerNtt("regular_1_l1");
      setDateRangePicTreeNtt([
        getDateOnly(seasonData[0]["seasonStart"]),
        clampSeasonEndToToday(seasonData[0]["seasonEnd"], todayFormatted),
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(seasonData[0]["seasonStart"]),
        clampSeasonEndToToday(seasonData[0]["seasonEnd"], todayFormatted),
      ]);
    }
  };

  const handleLayerChange = (event) => {
    setSelectedAreaLayerNtt(event.target.value);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div style={{ width: 300, marginTop: -12 }}>
                <SingleSelect
                  selected={seasonOption}
                  options={[
                    { name: "レギュラーシーズン1", value: "cmp4yz2lj00017rtcc4ok0jgv" },
                    { name: "東京・神奈川・千葉シーズン", value: "cmkdvml9l0001ngs48zepg7ne" },
                    { name: "栃木シーズン", value: "cmg7iw3vc00037bowekjy65pc" },
                    { name: "長野・山梨・埼玉シーズン", value: "cmg7iw3va00017bow596s39cs" },
                    { name: "北海道シーズン", value: "cmb68qvei00011vo0e591229z" },
                    { name: "岩手・宮城・福島・新潟シーズン", value: "cmd5oyfft0001iho10o2iceha" },
                    { name: "青森・秋田・山形シーズン", value: "cmadtlnih0001n3mx1wvl9zog" },
                    { name: "北関東シーズン", value: "cm64mqm94000br3n0esu8c0n4" },
                    { name: "秋田県北 & 県央・県南シーズン", value: "cm46zidp30003bpp6cxq101mo" }
                  ]}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
          {seasonOption.value === REGULAR_SEASON_ID &&
            currentPage === REGULAR_BY_AREA_PAGE && (
            <>
            <Grid item spacing={1}>
              階層：
            </Grid>
            <Grid item>
              <div style={{ width: 220, marginTop: -12 }}>
                <SingleSelect
                  selected={{ value: selectedAreaLayerId }}
                  options={REGULAR_LAYER_OPTIONS}
                  handleChange={handleLayerChange}
                />
              </div>
            </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

PeriodNtt.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeNtt.seasonData,
  selectedSeason: state.pictreeNtt.selectedSeason,
  selectedAreaLayerId: state.pictreeNtt.selectedAreaLayerId,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  currentPage: state.page.currentPage,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNtt: (param) => dispatch(setDateRangePicTreeNtt(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataNtt: () => dispatch(getSeasonDataNtt()),
  setSelectedSeasonNtt: (param) => dispatch(setSelectedSeasonNtt(param)),
  setSelectedAreaLayerNtt: (param) => dispatch(setSelectedAreaLayerNtt(param)),
  getByAreaData: (param) => dispatch(getByAreaData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodNtt));
